import { useDensity } from './chunk-WZJ5WOV7.js';
import { DataTableFacetedFilter, useDataTable, usePersistColumnVisibility, usePersistColumnWidths, usePersistColumnOrder, withPinnedColumnsVisible, readInitialColumnVisibility, readInitialColumnWidths, readInitialColumnOrder, TableSelectionBar, selectionColumn } from './chunk-5RURTZA4.js';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem } from './chunk-IJHDMW77.js';
import { Button } from './chunk-PLP233UC.js';
import { Settings2, Rows3 } from 'lucide-react';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { TABLE_DENSITIES, TABLE_DENSITY_LABELS } from '@authe/fe-commons/display';
import * as React3 from 'react';
import { createLogger } from '@authe/fe-commons/logging';

function DataTableViewOptions({
  table,
  labels,
  triggerLabel = "View"
}) {
  const columns = table.getAllColumns().filter((c) => c.getCanHide());
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { variant: "outline", size: "sm", className: "ml-auto hidden h-8 lg:flex", children: [
      /* @__PURE__ */ jsx(Settings2, { className: "mr-2 size-4" }),
      triggerLabel
    ] }) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", className: "w-[180px]", children: [
      /* @__PURE__ */ jsx(DropdownMenuLabel, { children: "Toggle columns" }),
      /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
      columns.map((column) => /* @__PURE__ */ jsx(
        DropdownMenuCheckboxItem,
        {
          className: "capitalize",
          checked: column.getIsVisible(),
          onCheckedChange: (value) => column.toggleVisibility(!!value),
          children: labels?.[column.id] ?? column.id
        },
        column.id
      ))
    ] })
  ] });
}
function DataTableDensityToggle({
  density,
  onDensityChange,
  triggerLabel = "Density",
  className
}) {
  const ctx = useDensity();
  const value = density ?? ctx.density;
  const setValue = onDensityChange ?? ctx.setDensity;
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { variant: "outline", size: "sm", className: className ?? "h-8", children: [
      /* @__PURE__ */ jsx(Rows3, { className: "mr-2 size-4" }),
      triggerLabel
    ] }) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", className: "w-[160px]", children: [
      /* @__PURE__ */ jsx(DropdownMenuLabel, { children: "Row density" }),
      /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
      TABLE_DENSITIES.map((d) => /* @__PURE__ */ jsx(
        DropdownMenuCheckboxItem,
        {
          checked: value === d,
          onCheckedChange: () => setValue(d),
          children: TABLE_DENSITY_LABELS[d]
        },
        d
      ))
    ] })
  ] });
}
function TableFacets({
  table,
  configs,
  rows
}) {
  const source = rows ?? table.getCoreRowModel().rows.map((row) => row.original);
  return /* @__PURE__ */ jsx(Fragment, { children: configs.map((config) => {
    const options = typeof config.options === "function" ? config.options(source) : config.options;
    if (options.length < (config.minOptions ?? 2)) return null;
    const column = table.getColumn(config.column);
    if (!column) return null;
    return /* @__PURE__ */ jsx(
      DataTableFacetedFilter,
      {
        column,
        title: config.title,
        options
      },
      config.column
    );
  }) });
}
function serverFacetColumn(args) {
  const { value, onChange, counts } = args;
  return {
    getFilterValue: () => value && value.length > 0 ? [...value] : void 0,
    setFilterValue: (updater) => {
      const next = typeof updater === "function" ? updater(value) : updater;
      const list = Array.isArray(next) ? next : void 0;
      onChange(list && list.length > 0 ? list : void 0);
    },
    getFacetedUniqueValues: () => counts ? new Map(counts) : /* @__PURE__ */ new Map()
  };
}
function optionsFromRows(rows, field) {
  const seen = /* @__PURE__ */ new Set();
  for (const row of rows) {
    const value = field(row);
    if (value) seen.add(value);
  }
  return Array.from(seen).map((value) => ({ label: value, value }));
}
var log = createLogger("ui-core:resource-table");
function defineResourceTable(config) {
  return config;
}
function useResourceTable(config, options = {}) {
  useStableConfigWarning(config);
  const viewKey = options.viewKey ?? config.id;
  const features = [...config.features ?? [], ...options.features ?? []];
  const source = source_(config);
  const sourceResult = source({
    ...options.query !== void 0 ? { query: options.query } : {},
    ...options.enabled !== void 0 ? { enabled: options.enabled } : {}
  });
  const configColumns = config.columns;
  const configLabels = config.columnLabels;
  const sourceRows = sourceResult.rows;
  const rowShape = sourceRows[0] ? Object.keys(sourceRows[0]).join(",") : "";
  const baseColumns = React3.useMemo(
    () => typeof configColumns === "function" ? configColumns(sourceRows) : configColumns,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configColumns, rowShape]
  );
  const columnLabels = React3.useMemo(
    () => typeof configLabels === "function" ? configLabels(sourceRows) : configLabels,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configLabels, rowShape]
  );
  let columns = baseColumns;
  let rows = sourceResult.rows;
  const preCtx = () => ({ id: config.id, viewKey, rows, columns });
  for (const feature of features) {
    if (feature.useRows) rows = feature.useRows(rows, preCtx());
  }
  for (const feature of features) {
    if (feature.useColumns) columns = feature.useColumns([...columns], preCtx());
  }
  let tableOptions = {};
  for (const feature of features) {
    if (feature.useTableOptions) {
      tableOptions = mergeTableOptions(tableOptions, feature.useTableOptions(preCtx()));
    }
  }
  const getRowId = config.getRowId;
  const rowIdOption = React3.useMemo(
    () => getRowId ? { getRowId: (row, index) => getRowId(row) ?? `idx:${index}` } : {},
    [getRowId]
  );
  const table = useDataTable({
    ...tableOptions,
    data: rows,
    columns,
    pageSize: config.pageSize ?? UNPAGED,
    initialSorting: tableOptions.initialSorting ?? config.initialSorting ?? [],
    initialColumnFilters: config.initialColumnFilters ?? [],
    // A multi-column search is a table option, not just a toolbar affordance —
    // and this table is the one the shell renders, so it has to be set here.
    ...config.search?.columns ? { searchColumns: config.search.columns } : {},
    tableOptions: { ...rowIdOption, ...tableOptions.tableOptions }
  });
  const tableCtx = { id: config.id, viewKey, rows, columns, table };
  const toolbarNodes = [];
  const bannerNodes = [];
  for (const feature of features) {
    if (feature.useToolbar) toolbarNodes.push(withKey(feature.id, feature.useToolbar(tableCtx)));
  }
  for (const feature of features) {
    if (feature.useBanner) bannerNodes.push(withKey(feature.id, feature.useBanner(tableCtx)));
  }
  return {
    table,
    rows,
    columns,
    ...getRowId ? { getRowId } : {},
    isLoading: sourceResult.isLoading,
    isFetching: sourceResult.isFetching,
    error: sourceResult.error,
    onRetry: sourceResult.refetch,
    toolbar: {
      ...config.search?.column ? { searchColumn: config.search.column } : {},
      ...config.search?.columns ? { searchColumns: config.search.columns } : {},
      ...config.search?.placeholder ? { searchPlaceholder: config.search.placeholder } : {},
      ...columnLabels ? { columnLabels } : {}
    },
    toolbarActions: toolbarNodes,
    banner: bannerNodes,
    ...sourceResult.pagination ? { pagination: sourceResult.pagination } : {},
    // No `viewKey`: persistence here is the `columnPrefs()` feature's, and
    // handing the shell the key too would have both writing it.
    ...options.onRowClick ? { onRowClick: options.onRowClick } : {},
    ...options.getRowHref ? { getRowHref: options.getRowHref } : {},
    ...options.restoreScroll ? { restoreScroll: options.restoreScroll } : {},
    ...options.activeRowId !== void 0 ? { activeRowId: options.activeRowId } : {},
    ...options.rowClassName ? { rowClassName: options.rowClassName } : {},
    ...options.height ? { height: options.height } : {},
    ...options.density ?? config.density ? { density: options.density ?? config.density } : {},
    ...config.emptyTitle ? { emptyTitle: config.emptyTitle } : {},
    ...config.emptyDescription ? { emptyDescription: config.emptyDescription } : {},
    ...options.emptyAction ? { emptyAction: options.emptyAction } : {},
    ...options.renderRowDetail ? { renderRowDetail: options.renderRowDetail } : {},
    ...options.detailTitle ? { detailTitle: options.detailTitle } : {},
    ...options.detailEyebrow ? { detailEyebrow: options.detailEyebrow } : {},
    ...options.detailSubtitle ? { detailSubtitle: options.detailSubtitle } : {},
    ...options.detailFooter ? { detailFooter: options.detailFooter } : {},
    ...options.className ? { className: options.className } : {}
  };
}
var UNPAGED = 1e5;
function source_(config) {
  return config.source;
}
function withKey(id, node) {
  return node == null || node === false ? null : React3.createElement(React3.Fragment, { key: id }, node);
}
function mergeTableOptions(base, next) {
  return {
    ...base,
    ...next,
    ...base.tableOptions || next.tableOptions ? { tableOptions: { ...base.tableOptions, ...next.tableOptions } } : {}
  };
}
function useStableConfigWarning(config) {
  const shape = (config.features ?? []).map((feature) => feature.id).join(">");
  const [first] = React3.useState(() => ({ source: config.source, shape }));
  if (process.env.NODE_ENV === "production" || warnedConfigs.has(config.id)) return;
  const changed = first.source !== config.source ? "source" : first.shape !== shape ? "features" : null;
  if (changed) {
    warnedConfigs.add(config.id);
    log.warn(
      `resource table config changed its ${changed} between renders, which changes the hooks that run \u2014 memoise that part for the component's lifetime`,
      { id: config.id, was: first.shape, now: shape }
    );
  }
}
var warnedConfigs = /* @__PURE__ */ new Set();
function columnPrefs(options = {}) {
  const { defaultVisibility } = options;
  return {
    id: "columnPrefs",
    useTableOptions: (ctx) => {
      const initialVisibility = React3.useMemo(
        () => withPinnedColumnsVisible(
          readInitialColumnVisibility(ctx.viewKey, defaultVisibility),
          ctx.columns
        ),
        // The surface default seeds only the FIRST render, so re-reading it when
        // the object identity changes would look reactive without being so.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ctx.viewKey]
      );
      const initialSizing = React3.useMemo(
        () => readInitialColumnWidths(ctx.viewKey),
        [ctx.viewKey]
      );
      const initialOrder = React3.useMemo(
        () => readInitialColumnOrder(ctx.viewKey, ctx.columns),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ctx.viewKey]
      );
      return {
        initialColumnVisibility: initialVisibility,
        initialColumnSizing: initialSizing,
        initialColumnOrder: initialOrder
      };
    },
    // Persistence needs the built table, and the toolbar hook is the phase that
    // has one. It contributes no UI.
    useToolbar: (ctx) => {
      usePersistColumnVisibility(ctx.table, ctx.viewKey);
      usePersistColumnWidths(ctx.table, ctx.viewKey);
      usePersistColumnOrder(ctx.table, ctx.viewKey);
      return null;
    }
  };
}
function columnResizing(options = {}) {
  const { enabled } = options;
  return {
    id: "columnResizing",
    useTableOptions: () => ({ enableColumnResizing: enabled ? enabled() : true })
  };
}
function selection(options = {}) {
  const { actions, enabled } = options;
  const on = () => enabled ? enabled() : true;
  return {
    id: "selection",
    useColumns: (columns) => on() ? [selectionColumn(), ...columns] : columns,
    useTableOptions: () => ({ enableRowSelection: on() }),
    useBanner: (ctx) => {
      if (!actions || !on()) return null;
      return /* @__PURE__ */ jsx(
        TableSelectionBar,
        {
          selected: ctx.table.getSelectedRowModel().rows.map((row) => row.original),
          clearSelection: () => ctx.table.resetRowSelection(),
          actions
        }
      );
    }
  };
}
function toolbarSlot(id, render) {
  return { id, useToolbar: (ctx) => render(ctx) };
}
function bannerSlot(id, render) {
  return { id, useBanner: (ctx) => render(ctx) };
}
var ROW_ARRIVAL_CLASS = "authe-row-arrival";
var NO_IDS = /* @__PURE__ */ new Set();
var MAX_SEEN = 1e4;
function useRowArrivalHighlight(liveRows, rowId, durationMs = 4e3) {
  const seen = React3.useRef(new Set(liveRows.map(rowId).filter((id) => Boolean(id))));
  const [expiries, setExpiries] = React3.useState(() => /* @__PURE__ */ new Map());
  const [count, setCount] = React3.useState(0);
  const [lastArrivedAt, setLastArrivedAt] = React3.useState(null);
  React3.useEffect(() => {
    const incoming = [];
    for (const row of liveRows) {
      const id = rowId(row);
      if (!id || seen.current.has(id)) continue;
      seen.current.add(id);
      incoming.push(id);
    }
    while (seen.current.size > MAX_SEEN) {
      seen.current.delete(seen.current.values().next().value);
    }
    if (!incoming.length) return;
    const now = Date.now();
    setCount((n) => n + incoming.length);
    setLastArrivedAt(now);
    setExpiries((previous) => {
      const next = new Map(previous);
      for (const id of incoming) next.set(id, now + durationMs);
      return next;
    });
  }, [liveRows, rowId, durationMs]);
  React3.useEffect(() => {
    if (!expiries.size) return;
    const nextExpiry = Math.min(...expiries.values());
    const timer = setTimeout(() => {
      const now = Date.now();
      setExpiries((previous) => new Map([...previous].filter(([, until]) => until > now)));
    }, Math.max(0, nextExpiry - Date.now()));
    return () => clearTimeout(timer);
  }, [expiries]);
  const ids = React3.useMemo(() => expiries.size ? new Set(expiries.keys()) : NO_IDS, [expiries]);
  return { ids, count, lastArrivedAt };
}

export { DataTableDensityToggle, DataTableViewOptions, ROW_ARRIVAL_CLASS, TableFacets, bannerSlot, columnPrefs, columnResizing, defineResourceTable, optionsFromRows, selection, serverFacetColumn, toolbarSlot, useResourceTable, useRowArrivalHighlight };
//# sourceMappingURL=chunk-G7D4PMGC.js.map
//# sourceMappingURL=chunk-G7D4PMGC.js.map